import { useRef, useState } from 'react';
import { atom, useRecoilState, useRecoilValue } from 'recoil';
import { usePopper } from 'react-popper';
import classNames from 'classnames';
import { Add, Remove, Search } from '@mui/icons-material';
import isString from 'lodash/isString';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { UrlParams } from 'common/types';
import { useOutsideEvent } from 'hooks/useOutsideEvent';
import { quote, unquote } from 'common/util/string';
import { getLinkForRoute } from 'common/util/location';
import { AddTermToFilterParams, PARSE_TYPE, TOKEN_TYPE } from 'common/util/filter/types';
import { tokenizeFilter } from 'common/util/filter/parse';
import { addTermToFilter } from 'common/util/filter/filter';

import Link from 'components/common/Link/Link';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { draftSearchParamState, eventParamsState } from 'components/common/Managers/RouteStateManager.state';

import './LoglineSelectionPopover.scss';

export type LoglineSelectionState = {
  text: string;
  type: TOKEN_TYPE | PARSE_TYPE;
  field: string;
  partial: boolean;
};

export const loglineSelectionStateDefault: LoglineSelectionState = {
  text: '',
  type: TOKEN_TYPE.UNKNOWN as TOKEN_TYPE | PARSE_TYPE,
  field: '',
  partial: false,
};

export const loglineSelectionState = atom<LoglineSelectionState>({
  key: 'loglineSelectionState',
  default: loglineSelectionStateDefault,
});

export const virtualReferenceState = atom({
  key: 'virtualReferenceState',
  default: {
    getBoundingClientRect() {
      return {
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        width: 0,
        height: 0,
      };
    },
  },
});

/**
 * Popover component that displays when a user selects some text on a logline.
 * Gives the user some options to use their selection in the search filter.
 */
function LoglineSelectionPopover(): JSX.Element {
  const virtualReference = useRecoilValue(virtualReferenceState);
  const [loglineSelection, setLoglineSelection] = useRecoilState(loglineSelectionState);
  const isPopoverVisible = loglineSelection.text.length > 0;
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(virtualReference, popperElement, {
    placement: 'top',
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } }, // tells popper to calculate arrow placement
      {
        name: 'offset',
        options: {
          offset: [0, 10], // a bit of offset positioning so popover doesn't cover logline
        },
      },
    ],
  });

  const eventParams = useRecoilValue(eventParamsState);
  const draftParams = useRecoilValue(draftSearchParamState);

  const handleMousedown = (): void => {
    if (isPopoverVisible) {
      setLoglineSelection(loglineSelectionStateDefault);
    }
  };

  // handle outside mousedown event
  useOutsideEvent('mousedown', [containerRef], handleMousedown);

  if (!loglineSelection?.text) {
    return <></>;
  }

  // create a filter term based on info about the current selection
  const getNewTerm = (): AddTermToFilterParams => {
    const type = loglineSelection.type;
    const text = loglineSelection.text;

    if (type === TOKEN_TYPE.UNKNOWN) {
      return {
        filter: '',
        text: text ? quote(unquote(text.trim())) : '',
      };
    }

    if (type === PARSE_TYPE.TERM) {
      const tokens = tokenizeFilter(text);
      if (tokens?.length === 3) {
        return {
          filter: '',
          field: tokens[0].text,
          op: tokens[1].text,
          value: tokens[2].text,
        };
      }
    }

    if (
      loglineSelection.field &&
      [TOKEN_TYPE.STRING, TOKEN_TYPE.NUMBER, TOKEN_TYPE.BOOLEAN].includes(type as TOKEN_TYPE)
    ) {
      const isStringType = type === TOKEN_TYPE.STRING && isString(text);
      return {
        filter: '',
        field: loglineSelection.field,
        op: isStringType && loglineSelection.partial ? 'contains' : '=',
        value: isStringType ? quote(unquote(text)) : text,
      };
    }

    return { filter: '' };
  };

  const close = (): void => {
    setLoglineSelection(loglineSelectionStateDefault);
  };

  const newLink = getLinkForRoute(K.ROUTE.EVENTS, eventParams, { filter: addTermToFilter(getNewTerm()) } as UrlParams);
  const addFilter = addTermToFilter({ ...getNewTerm(), filter: draftParams[K.URL_PARAM.FILTER] });
  const addLink = getLinkForRoute(K.ROUTE.EVENTS, eventParams, { filter: addFilter } as UrlParams);
  const excludeFilter = addTermToFilter({ ...getNewTerm(), filter: draftParams[K.URL_PARAM.FILTER], negate: true });
  const excludeLink = getLinkForRoute(K.ROUTE.EVENTS, eventParams, { filter: excludeFilter } as UrlParams);

  const addIcon = <Add fontSize="small" />;
  const removeIcon = <Remove fontSize="small" />;
  const searchIcon = <Search fontSize="small" />;
  const hidden = !isPopoverVisible ? 'LoglineSelectionPopover__PopoverContainer--hidden' : '';

  return (
    <div ref={containerRef} className="LoglineSelectionPopover">
      <div
        className={classNames('LoglineSelectionPopover__PopoverContainer', hidden)}
        ref={setPopperElement}
        style={{ ...styles.popper }}
        {...attributes.popper}
      >
        <Link className="LoglineSelectionPopover__Link" href={newLink} onClick={close}>
          <Button
            className="LoglineSelectionPopover__Button"
            leftIcon={searchIcon}
            variant={ButtonVariant.BORDERLESS}
            testId={ELEMENT.searchLogsSelectionNew}
          >
            New
          </Button>
        </Link>
        <Link className="LoglineSelectionPopover__Link" href={addLink} onClick={close}>
          <Button
            className="LoglineSelectionPopover__Button"
            leftIcon={addIcon}
            variant={ButtonVariant.BORDERLESS}
            testId={ELEMENT.searchLogsSelectionAdd}
          >
            Add
          </Button>
        </Link>
        <Link className="LoglineSelectionPopover__Link" href={excludeLink} onClick={close}>
          <Button
            className="LoglineSelectionPopover__Button"
            leftIcon={removeIcon}
            variant={ButtonVariant.BORDERLESS}
            testId={ELEMENT.searchLogsSelectionExclude}
          >
            Exclude
          </Button>
        </Link>
        <div className="LoglineSelectionPopover__Arrow" ref={setArrowElement} style={styles.arrow} />
      </div>
    </div>
  );
}

export default LoglineSelectionPopover;
